'use client';
import L from 'leaflet';
import dynamic from 'next/dynamic';
import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@app/libs/utils';

import ArrowExpandIcon from '../icons/ArrowExpandIcon';
import PlusIcon from '../icons/PlusIcon';

import type { MapType } from './LeafletMapComponent';

interface MapViewProps {
  lat?: number;
  lng?: number;
  onLocationChange?: (lat: number, lng: number) => void;
  height?: string;
  width?: string;
  className?: string;
  lock?: boolean; // Khóa việc di chuyển marker
  mapType?: MapType; // Loại bản đồ
  showMapTypeControl?: boolean; // Hiển thị nút chuyển đổi loại bản đồ
  radius?: number; // Bán kính tính bằng mét
  onRadiusChange?: (radius: number) => void; // Callback khi radius thay đổi
  showRadiusSlider?: boolean; // Hiển thị slider điều chỉnh radius
  markers?: { lat: number; lng: number; toolip: React.ReactNode; popup: React.ReactNode }[];
  disableScrollZoom?: boolean;
  expandable?: boolean;
}

// Dynamic import Leaflet component
const LeafletMapComponent = dynamic(() => import('./LeafletMapComponent'), {
  ssr: false,
  loading: () => (
    <div
      style={{
        height: '400px',
        width: '100%',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f3f4f6',
      }}
    >
      <div className="text-gray-500">Đang tải bản đồ...</div>
    </div>
  ),
});

const MapView: React.FC<MapViewProps> = ({
  lat,
  lng,
  onLocationChange,
  height = '400px',
  width = '100%',
  className = '',
  lock = false,
  mapType = 'roadmap',
  showMapTypeControl = true,
  radius,
  onRadiusChange,
  showRadiusSlider = false,
  markers,
  disableScrollZoom = false,
  expandable,
}) => {
  const [isClient, setIsClient] = useState(false);
  const [currentRadius, setCurrentRadius] = useState<number | undefined>(radius);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [dim, setDim] = useState<string[]>([width, height]);
  const map = useRef<L.Map>(null);

  // Default location: Hà Nội
  const defaultLat = 21.0285;
  const defaultLng = 105.8542;

  const currentLat = lat ?? defaultLat;
  const currentLng = lng ?? defaultLng;

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Cập nhật currentRadius khi radius prop thay đổi
  useEffect(() => {
    if (radius !== undefined) {
      setCurrentRadius(radius);
    }
  }, [radius]);

  const handleRadiusChange = (value: number) => {
    setCurrentRadius(value);
    onRadiusChange?.(value);
  };

  useEffect(() => {
    const container = map.current?.getContainer();

    if (isExpanded) {
      setDim(['100%', 'calc(100vh - 172px)']);
      if (container) {
        container.style.width = '100%';
        container.style.height = 'calc(100vh - 172px)';
      }
    } else {
      setDim([width, height]);
      if (container) {
        container.style.width = width;
        container.style.height = height;
      }
    }

    if (map.current) {
      map.current.invalidateSize();
      map.current.setView([currentLat, currentLng], 15, {
        animate: true,
        duration: 1.5,
      });
    }
  }, [isExpanded, width, height, currentLat, currentLng]);

  const formatRadius = (meters: number): string => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)} km`;
    }
    return `${meters} m`;
  };

  if (!isClient) {
    return (
      <div
        className={`map-container ${className}`}
        style={{
          height: dim[1],
          width: dim[0],
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f3f4f6',
        }}
      >
        <div className="text-gray-500">Đang tải bản đồ...</div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'map-container z-[99999]',
        className,
        isExpanded ? 'absolute top-4 left-0' : 'relative'
      )}
      style={{ height: dim[1], width: dim[0] }}
    >
      <LeafletMapComponent
        lat={currentLat}
        lng={currentLng}
        onLocationChange={onLocationChange}
        height="100%"
        width="100%"
        className={className}
        lock={lock}
        mapType={mapType}
        showMapTypeControl={showMapTypeControl}
        radius={currentRadius}
        markers={markers}
        disableScrollZoom={disableScrollZoom}
        onRendered={(m: L.Map) => (map.current = m)}
      />
      {showRadiusSlider && (
        <div
          className={cn(
            'absolute bottom-20 left-0 md:left-1/2 transform md:-translate-x-1/2 bg-white p-4 rounded-lg box-shadow-md z-100 w-280',
            'max-w-[calc(100vw-48px)]'
          )}
        >
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <label className="text-sm font-medium text-gray-700">Bán kính tìm kiếm</label>
              <span className="text-sm font-bold text-blue-600 min-w-16 text-right">
                {currentRadius ? formatRadius(currentRadius) : ''}
              </span>
            </div>
          </div>
        </div>
      )}
      {expandable && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="absolute top-4 right-4 z-[99999] bg-white p-1 border-1 rounded cursor-pointer shadow-lg"
        >
          {isExpanded ? (
            <PlusIcon className="rotate-45" size={20}></PlusIcon>
          ) : (
            <ArrowExpandIcon size={20}></ArrowExpandIcon>
          )}
        </button>
      )}
    </div>
  );
};

export default MapView;
